import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Link, Text, TextProps } from '@chakra-ui/react';

interface ExpandableTextProps extends TextProps {
    children: string;
    maxLines?: number;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
    children,
    maxLines = 3,
    ...textProps
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!children) {
            return;
        }

        const checkTruncation = () => {
            if (textRef.current) {
                const lineHeight = parseFloat(getComputedStyle(textRef.current).lineHeight);
                const maxHeight = lineHeight * maxLines;
                setIsTruncated(textRef.current.scrollHeight > maxHeight);
            }
        };

        // Check truncation after render
        const timeoutId = setTimeout(checkTruncation, 0);

        // Debounce resize events
        let resizeTimeoutId: number;

        const handleResize = () => {
            clearTimeout(resizeTimeoutId);
            resizeTimeoutId = window.setTimeout(checkTruncation, 150);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timeoutId);
            clearTimeout(resizeTimeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [children, maxLines]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
    }, []);

    if (!children) {
        return null;
    }

    return (
        <Box>
            <Text ref={textRef} {...textProps} noOfLines={isExpanded ? undefined : maxLines}>
                {children}
            </Text>
            {isTruncated && (
                <Link
                    color="primary.400"
                    fontSize="14px"
                    textDecoration="underline"
                    onClick={handleToggle}
                    cursor="pointer"
                >
                    {isExpanded ? 'See Less' : 'See More'}
                </Link>
            )}
        </Box>
    );
};

export default ExpandableText;
